import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const VALID_TABS = ['profile', 'communication', 'defaults', 'carrier-bga'];

interface UseAdvisorTabsProps {
  hasUnsavedChanges: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
}

export function useAdvisorTabs({ hasUnsavedChanges, setShowUnsavedDialog }: UseAdvisorTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const tabFromUrl = searchParams.get('tab');
  const initialTab = VALID_TABS.includes(tabFromUrl || '') ? tabFromUrl : 'profile';
  const [activeTab, setActiveTab] = useState(initialTab || 'profile');

  // Update activeTab when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  const navigateToTab = (tabValue: string) => {
    setActiveTab(tabValue);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabValue);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleTabChange = (tabValue: string) => {
    if (hasUnsavedChanges && tabValue !== activeTab) {
      // Store the tab the user wants to navigate to
      setPendingTab(tabValue);
      setShowUnsavedDialog(true);
    } else {
      navigateToTab(tabValue);
    }
  };

  const handleDialogCancel = () => {
    if (pendingTab) {
      navigateToTab(pendingTab);
      setPendingTab(null);
    }
  };

  const handleDialogConfirm = () => {
    if (pendingTab) {
      navigateToTab(pendingTab);
      setPendingTab(null);
    }
  };

  return {
    activeTab,
    handleTabChange,
    pendingTab,
    handleDialogCancel,
    handleDialogConfirm,
  };
}
